import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { MOCK_SIGNED_OUT_KEY, clearToken, getToken } from '@/lib/api-client';

export type UserRole = 'admin' | 'support' | string;

export interface AuthUser {
  id: string | number;
  email?: string;
  role: UserRole;
}

interface AuthContextValue {
  user: AuthUser | null;
  role: UserRole | null;
  isAdmin: boolean;
  isSupport: boolean;
  /** True when the role may view the Fleet section at all. */
  canViewFleet: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Decodes a JWT payload without verifying the signature (the API verifies). */
function decodeJwt(token: string): Record<string, unknown> | null {
  const parts = token.split('.');

  if (parts.length !== 3) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );

    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function parseAuthUser(token: string | null): AuthUser | null {
  if (!token) return null;

  const claims = decodeJwt(token);

  if (!claims) return null;

  // Real API access tokens nest identity under `user`
  // ({ user: { id, role }, type, iat, exp }). The dev/mock token is flat
  // ({ sub, role, email }). Accept either shape.
  const nested = (claims.user as Record<string, unknown> | undefined) ?? {};

  const role = nested.role ?? claims.role;
  const id = nested.id ?? claims.sub ?? claims.id;
  const email = nested.email ?? claims.email;

  if (typeof role !== 'string' || (typeof id !== 'string' && typeof id !== 'number')) {
    return null;
  }

  return {
    id,
    role,
    email: typeof email === 'string' ? email : undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const user = useMemo(() => parseAuthUser(getToken()), []);

  const logout = useCallback(() => {
    // All three keys: a leftover expiry would be checked against the next token
    // written without setToken() (seedMockJwt), and the refresh token must not
    // outlive sign-out (OBV-0048).
    clearToken();
    // Mock mode auto-seeds a JWT on every load; without this flag a sign-out
    // would be immediately undone by the re-seed on the /login reload.
    sessionStorage.setItem(MOCK_SIGNED_OUT_KEY, '1');
    window.location.assign('/login');
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const role = user?.role ?? null;

    return {
      user,
      role,
      isAdmin: role === 'admin',
      isSupport: role === 'support',
      canViewFleet: role === 'admin' || role === 'support',
      logout,
    };
  }, [user, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);

  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');

  return ctx;
}
